import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminDashboard from './pages/AdminDashboard';
import ProducerDashboard from './pages/ProducerDashboard';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import ProducerCropsPage from './pages/ProducerCropsPage';
import ProducerTrainingsPage from './pages/ProducerTrainingsPage';
import ProducerLogisticsPage from './pages/ProducerLogisticsPage';
import ProducerProfilePage from './pages/ProducerProfilePage';
import ProducersPage from './pages/ProducersPage-simple';
import CropsPage from './pages/CropsPage';
import SuppliesPage from './pages/SuppliesPage';
import TrainingPage from './pages/TrainingPage';
import CommercePage from './pages/CommercePage';
import ReportsPage from './pages/ReportsPage';
import LogisticsPage from './pages/LogisticsPage';
import MapPage from './pages/MapPage';

// Components & Layout
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';
import { useAuth } from './contexts/AuthContext';

function LayoutWrapper({ children, title }) {
  return <MainLayout title={title}>{children}</MainLayout>;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth Pages */}
      <Route
        path="/login"
        element={
          isAuthenticated ?
            <Navigate to={user?.role === 'admin' ? '/admin' : '/producer'} replace /> :
            <LoginPage />
        }
      />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LayoutWrapper title="Panel General de Administración">
              <AdminDashboard />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LayoutWrapper title="Panel de Control Ejecutivo">
              <ExecutiveDashboardPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/producers"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LayoutWrapper title="Censo y Registro de Productores">
              <ProducersPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* Protected Producer Routes */}
      <Route
        path="/producer"
        element={
          <ProtectedRoute allowedRoles={['producer']}>
            <LayoutWrapper title="Panel Principal del Productor">
              <ProducerDashboard />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/producer/crops"
        element={
          <ProtectedRoute allowedRoles={['producer']}>
            <LayoutWrapper title="Gestión de Mis Cultivos">
              <ProducerCropsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/producer/trainings"
        element={
          <ProtectedRoute allowedRoles={['producer']}>
            <LayoutWrapper title="Capacitaciones y Formación">
              <ProducerTrainingsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/producer/logistics"
        element={
          <ProtectedRoute allowedRoles={['producer']}>
            <LayoutWrapper title="Logística y Transporte">
              <ProducerLogisticsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/producer/profile"
        element={
          <ProtectedRoute allowedRoles={['producer']}>
            <LayoutWrapper title="Mi Perfil de Productor">
              <ProducerProfilePage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* Shared Protected Routes (Admin & Producer) */}
      <Route
        path="/crops"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Monitoreo General de Cultivos">
              <CropsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Inventario y Control de Insumos">
              <SuppliesPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Programa de Formación Agrícola">
              <TrainingPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/commerce"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Comercio y Mercado Comunal">
              <CommercePage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logistics"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Logística e Insumos">
              <LogisticsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Analítica y Consultor IA">
              <ReportsPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/map"
        element={
          <ProtectedRoute allowedRoles={['admin', 'producer']}>
            <LayoutWrapper title="Mapa de Geolocalización Comunal">
              <MapPage />
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <Helmet>
        <title>SISCIAC - Sistema de Información sobre Soberanía Alimentaria</title>
        <meta name="description" content="Plataforma integral para el control y gestión de procesos agrícolas comunales." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      </Helmet>
      <div className="min-h-screen bg-slate-100 font-sans">
        <AppRoutes />
      </div>
    </Router>
  );
}

export default App;
